import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TrackingMap } from '@/components/TrackingMap';
import { MapPin, Users, Eye, Globe, RefreshCcw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';

interface WebsiteAccess {
  id: string;
  user_id?: string;
  session_id: string;
  ip_address?: string | null;
  latitude?: number;
  longitude?: number;
  accuracy?: number;
  city?: string;
  country?: string;
  page_path?: string;
  created_at: string;
  last_seen: string;
}

interface MapLocation {
  latitude: number;
  longitude: number;
  title?: string;
  description?: string;
  type: 'customer' | 'tradesman' | 'current';
}

export const AdminLiveAccessMap: React.FC = () => {
  const [accessData, setAccessData] = useState<WebsiteAccess[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    totalVisitors: 0,
    activeNow: 0,
    uniqueLocations: 0,
    topCountries: [] as Array<{ country: string; count: number }>
  });
  const { toast } = useToast();

  const fetchLiveAccess = async () => {
    try {
      // Fetch recent website access data (last 24 hours)
      const { data, error } = await supabase
        .rpc('get_website_access_24h');

      if (error) {
        console.error('Error fetching website access:', error);
        toast({
          title: "Error",
          description: "Failed to load website access data",
          variant: "destructive",
        });
        return;
      }

      // Type cast the data to ensure proper typing
      const typedData: WebsiteAccess[] = (data || []).map(item => ({
        ...item,
        ip_address: item.ip_address as string | null
      }));

      // Deduplicate by IP address, keeping only the most recent visit per IP
      const uniqueByIP = typedData.reduce((acc, current) => {
        const ip = current.ip_address || current.session_id; // Fallback to session_id if no IP
        const existing = acc.get(ip);
        
        if (!existing || new Date(current.last_seen) > new Date(existing.last_seen)) {
          acc.set(ip, current);
        }
        
        return acc;
      }, new Map<string, WebsiteAccess>());

      const deduplicatedData = Array.from(uniqueByIP.values())
        .sort((a, b) => new Date(b.last_seen).getTime() - new Date(a.last_seen).getTime());

      setAccessData(deduplicatedData);
      
      // Calculate stats
      const now = new Date();
      const fiveMinutesAgo = new Date(now.getTime() - 5 * 60 * 1000);
      
      const activeNow = deduplicatedData.filter(access => 
        new Date(access.last_seen) > fiveMinutesAgo
      ).length;

      const uniqueLocations = new Set(
        deduplicatedData.filter(access => access.latitude && access.longitude)
          .map(access => `${access.latitude},${access.longitude}`)
      ).size;

      const countryCounts = deduplicatedData.reduce((acc, access) => {
        if (access.country) {
          acc[access.country] = (acc[access.country] || 0) + 1;
        }
        return acc;
      }, {} as Record<string, number>);

      const topCountries = Object.entries(countryCounts)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 5)
        .map(([country, count]) => ({ country, count }));

      setStats({
        totalVisitors: deduplicatedData.length,
        activeNow,
        uniqueLocations,
        topCountries
      });

      if (deduplicatedData.length > 0) {
        toast({
          title: "Loaded",
          description: `${deduplicatedData.length} unique visitors in last 24h`,
        });
      }

    } catch (error) {
      console.error('Error in fetchLiveAccess:', error);
      toast({
        title: "Error",
        description: "Failed to load live access data",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLiveAccess();

    // Set up real-time subscription
    const channel = supabase
      .channel(`live-website-access-${crypto.randomUUID()}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'website_access'
        },
        (payload) => {
          console.log('Live access update:', payload);
          fetchLiveAccess(); // Refresh data on any change
        }
      )
      .subscribe();

    // Refresh data every 30 seconds
    const interval = setInterval(fetchLiveAccess, 30000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(interval);
    };
  }, []);

  const mapLocations: MapLocation[] = accessData
    .filter(access => access.latitude && access.longitude)
    .map(access => ({
      latitude: access.latitude!,
      longitude: access.longitude!,
      title: `${access.city || 'Unknown'}, ${access.country || 'Unknown'}`,
      description: `Page: ${access.page_path || '/'}\nLast seen: ${new Date(access.last_seen).toLocaleString()}`,
      type: 'current' as const
    }));

  const getActivityStatus = (lastSeen: string) => {
    const now = new Date();
    const lastSeenDate = new Date(lastSeen);
    const minutesAgo = (now.getTime() - lastSeenDate.getTime()) / (1000 * 60);
    
    if (minutesAgo < 5) return { label: 'Active', variant: 'default' as const };
    if (minutesAgo < 30) return { label: 'Recent', variant: 'secondary' as const };
    return { label: 'Inactive', variant: 'outline' as const };
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="animate-pulse">
                  <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                  <div className="h-8 bg-muted rounded w-1/2"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="animate-pulse">
              <div className="h-96 bg-muted rounded"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Visitors (24h)</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalVisitors}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Now</CardTitle>
            <Eye className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats.activeNow}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Unique Locations</CardTitle>
            <MapPin className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.uniqueLocations}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Top Country</CardTitle>
            <Globe className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats.topCountries[0]?.country || 'N/A'}
            </div>
            {stats.topCountries[0] && (
              <p className="text-xs text-muted-foreground">
                {stats.topCountries[0].count} visitors
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="relative z-10 flex flex-row items-center justify-between">
            <CardTitle>Live Access Map</CardTitle>
            <Button variant="secondary" size="sm" onClick={fetchLiveAccess}>
              <RefreshCcw className="h-4 w-4 mr-2" /> Refresh
            </Button>
          </CardHeader>
          <CardContent>
            {mapLocations.length > 0 ? (
              <TrackingMap
                locations={mapLocations}
                className="h-96"
                centerLat={51.505}
                centerLng={-0.09}
                zoom={2}
                showNavigation={true}
                interactive={true}
                fitToLocations={true}
              />
            ) : (
              <div className="h-96 flex items-center justify-center bg-muted rounded-lg">
                <p className="text-muted-foreground">No location data available</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {accessData.slice(0, 20).map((access) => {
                const status = getActivityStatus(access.last_seen);
                return (
                  <div key={access.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant={status.variant} className="text-xs">
                          {status.label}
                        </Badge>
                        <span className="text-sm font-medium">
                          {access.city || 'Unknown'}, {access.country || 'Unknown'}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Page: {access.page_path || '/'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(access.last_seen).toLocaleString()}
                      </p>
                    </div>
                  </div>
                );
              })}
              
              {accessData.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">No recent activity</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Top Countries (24h)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {stats.topCountries.map((country, index) => (
              <div key={country.country} className="text-center p-4 bg-muted/50 rounded-lg">
                <div className="text-lg font-bold">{country.count}</div>
                <div className="text-sm text-muted-foreground">{country.country}</div>
              </div>
            ))}
          </div>
          
          {stats.topCountries.length === 0 && (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No country data available</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};